// ═══════════════════════════════════════════════════════
// ICON SYSTEM — SVG icons inline (line-thin 1.5px stroke)
// Brand: single-color, no fill, uniform weight
// ═══════════════════════════════════════════════════════
import type { CSSProperties, ReactNode } from "react";

const DEFAULT_SIZE = 18;
const DEFAULT_STROKE = 1.5;

interface IconBaseProps {
  children?: ReactNode;
  size?: number;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
}

/** Base SVG wrapper — all icons share this */
function I({ children, size = DEFAULT_SIZE, className, style, ariaLabel }: IconBaseProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={DEFAULT_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flexShrink: 0, ...style }}
      role={ariaLabel ? "img" : "presentation"}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : "true"}
    >
      {children}
    </svg>
  );
}

/** Props for all individual icon components */
export type IconProps = Omit<IconBaseProps, "children">;

// ── Navigation Icons ──────────────────────────────────

export function IconDashboard(p: IconProps) {
  return (
    <I {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="4" rx="1" />
      <rect x="14" y="11" width="7" height="10" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </I>
  );
}
export function IconBriefcase(p: IconProps) {
  return (
    <I {...p}>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </I>
  );
}
export function IconTrendingUp(p: IconProps) {
  return (
    <I {...p}>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </I>
  );
}
export function IconBarChart(p: IconProps) {
  return (
    <I {...p}>
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </I>
  );
}
export function IconFileText(p: IconProps) {
  return (
    <I {...p}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </I>
  );
}
export function IconDollarSign(p: IconProps) {
  return (
    <I {...p}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </I>
  );
}
export function IconRefreshCw(p: IconProps) {
  return (
    <I {...p}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </I>
  );
}
export function IconTarget(p: IconProps) {
  return (
    <I {...p}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </I>
  );
}
export function IconZap(p: IconProps) {
  return (
    <I {...p}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </I>
  );
}
export function IconClipboard(p: IconProps) {
  return (
    <I {...p}>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
    </I>
  );
}
export function IconCoins(p: IconProps) {
  return (
    <I {...p}>
      <circle cx="8" cy="8" r="6" />
      <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
      <line x1="7" y1="6" x2="7.01" y2="6" />
      <line x1="11" y1="18" x2="11.01" y2="18" />
    </I>
  );
}
export function IconFlask(p: IconProps) {
  return (
    <I {...p}>
      <path d="M9 3h6" />
      <path d="M10 9V3" />
      <path d="M14 9V3" />
      <path d="M6 21h12" />
      <path d="M10 9a7 7 0 0 0-4 10h12a7 7 0 0 0-4-10" />
    </I>
  );
}
export function IconHome(p: IconProps) {
  return (
    <I {...p}>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </I>
  );
}
export function IconUpload(p: IconProps) {
  return (
    <I {...p}>
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </I>
  );
}
export function IconUmbrella(p: IconProps) {
  return (
    <I {...p}>
      <path d="M23 12a11 11 0 0 0-22 0" />
      <path d="M12 12v9a3 3 0 0 0 6 0" />
      <line x1="12" y1="1" x2="12" y2="3" />
    </I>
  );
}
export function IconBookOpen(p: IconProps) {
  return (
    <I {...p}>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </I>
  );
}
export function IconUser(p: IconProps) {
  return (
    <I {...p}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </I>
  );
}

// ── Utility Icons ─────────────────────────────────────

export function IconBell(p: IconProps) {
  return (
    <I {...p}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </I>
  );
}
export function IconSun(p: IconProps) {
  return (
    <I {...p}>
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </I>
  );
}
export function IconMoon(p: IconProps) {
  return (
    <I {...p}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </I>
  );
}
export function IconLogOut(p: IconProps) {
  return (
    <I {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </I>
  );
}
export function IconChevronLeft(p: IconProps) {
  return (
    <I {...p}>
      <polyline points="15 18 9 12 15 6" />
    </I>
  );
}
export function IconChevronRight(p: IconProps) {
  return (
    <I {...p}>
      <polyline points="9 18 15 12 9 6" />
    </I>
  );
}
export function IconChevronDown(p: IconProps) {
  return (
    <I {...p}>
      <polyline points="6 9 12 15 18 9" />
    </I>
  );
}
export function IconMenu(p: IconProps) {
  return (
    <I {...p}>
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </I>
  );
}
export function IconX(p: IconProps) {
  return (
    <I {...p}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </I>
  );
}
export function IconInfo(p: IconProps) {
  return (
    <I {...p}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </I>
  );
}
export function IconDownload(p: IconProps) {
  return (
    <I {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </I>
  );
}
export function IconLoader(p: IconProps) {
  return (
    <I {...p}>
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
    </I>
  );
}
export function IconRuler(p: IconProps) {
  return (
    <I {...p}>
      <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z" />
      <path d="m14.5 12.5 2-2" />
      <path d="m11.5 9.5 2-2" />
      <path d="m8.5 6.5 2-2" />
      <path d="m17.5 15.5 2-2" />
    </I>
  );
}

// ── Dashboard Settings Icons ──────────────────────────

export function IconSettings(p: IconProps) {
  return (
    <I {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v6m0 6v6" />
      <path d="M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24" />
      <path d="M1 12h6m6 0h6" />
      <path d="M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24" />
    </I>
  );
}

export function IconGripVertical(p: IconProps) {
  return (
    <I {...p}>
      <circle cx="9" cy="5" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="9" cy="19" r="1.5" />
      <circle cx="15" cy="5" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="15" cy="19" r="1.5" />
    </I>
  );
}

export function IconEye(p: IconProps) {
  return (
    <I {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </I>
  );
}

export function IconEyeOff(p: IconProps) {
  return (
    <I {...p}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </I>
  );
}

// ── Sorting Icons ──────────────────────────────────────

export function IconSortAsc(p: IconProps) {
  return (
    <I {...p}>
      <polyline points="3 22 3 2 13 12 3 2" />
      <line x1="16" y1="3" x2="23" y2="3" />
      <line x1="16" y1="8" x2="21" y2="8" />
      <line x1="16" y1="13" x2="18" y2="13" />
    </I>
  );
}

export function IconSortDesc(p: IconProps) {
  return (
    <I {...p}>
      <polyline points="3 2 3 22 13 12 3 22" />
      <line x1="16" y1="3" x2="23" y2="3" />
      <line x1="16" y1="8" x2="21" y2="8" />
      <line x1="16" y1="13" x2="18" y2="13" />
    </I>
  );
}

// ── Grid Icon ──────────────────────────────────────────

export function IconGrid(p: IconProps) {
  return (
    <I {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </I>
  );
}

// ── Succession Planning Icons ────────────────────────

export function IconUsers(p: IconProps) {
  return (
    <I {...p}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </I>
  );
}

export function IconBuilding(p: IconProps) {
  return (
    <I {...p}>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
    </I>
  );
}

export function IconShield(p: IconProps) {
  return (
    <I {...p}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </I>
  );
}

export function IconCalendarClock(p: IconProps) {
  return (
    <I {...p}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <circle cx="14" cy="16" r="3" />
      <path d="M14 14.5v1.5l1 1" />
    </I>
  );
}

export function IconShieldCheck(p: IconProps) {
  return (
    <I {...p}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </I>
  );
}
export function IconClock(p: IconProps) {
  return (
    <I {...p}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </I>
  );
}
export function IconCreditCard(p: IconProps) {
  return (
    <I {...p}>
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </I>
  );
}

export function IconCompass(p: IconProps) {
  return (
    <I {...p}>
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </I>
  );
}

// ── Icon Map for Nav Config ───────────────────────────
export const NAV_ICONS: Record<string, (p: IconProps) => JSX.Element> = {
  dashboard: IconDashboard,
  carteira: IconBriefcase,
  performance: IconTrendingUp,
  metricas: IconBarChart,
  relatorios: IconFileText,
  aportes: IconDollarSign,
  movimentacoes: IconRefreshCw,
  metas: IconTarget,
  calcir: IconZap,
  informeir: IconClipboard,
  custos: IconCoins,
  dividendos: IconDollarSign,
  cenarios: IconFlask,
  "patrimonio-total": IconHome,
  documentos: IconUpload,
  aposentadoria: IconUmbrella,
  educacao: IconBookOpen,
  perfil: IconUser,
  sucessao: IconUsers,
  "goal-mapping": IconTarget,
  hedge: IconShieldCheck,
  "schedule-delivery": IconClock,
  amortizacao: IconCreditCard,
  alocacao: IconCompass,
};
